import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useRouterHash } from '../use-router-hash';

type UseCollapseHash = (
  hash: string,
  additionalTags?: string[],
) => ReturnOfUseCollapseHash;

export type ReturnOfUseCollapseHash = {
  isCollapsed: boolean;
  collapseAll: () => Promise<boolean> | undefined;
  collapse: () => Promise<boolean> | undefined;
  collapseAsBack: () => void;
  popInstead: string;
  popUpon: string;
};
export const separator = '.';

export const useCollapseHash: UseCollapseHash = (
  hashValue,
  additionalHashes = [],
) => {
  const router = useRouter();
  const currentHash = useRouterHash();
  const currentHashArray = currentHash.split(separator);
  const [isCollapsed, setIsCollapsed] = useState(
    !currentHashArray.includes(hashValue),
  );

  const collapseAllPath = router.asPath.replace(`#${currentHash}`, '');
  const collapsePath = router.asPath
    .replace(
      currentHash,
      currentHashArray.filter((hash) => hash !== hashValue).join(separator),
    )
    .replace(/#$/, '');

  const collapse = () => {
    if (isCollapsed) return;

    return router.push(collapsePath, undefined, { scroll: false });
  };

  const collapseAsBack = () => {
    if (isCollapsed) return;

    if (window.history.length === 1) {
      return router.replace(collapsePath, undefined, { scroll: false });
    }

    return router.back();
  };

  const collapseAll = () => {
    if (router.asPath === collapseAllPath) return;

    return router.push(collapseAllPath, undefined, { scroll: false });
  };

  useEffect(() => {
    setIsCollapsed(!currentHashArray.includes(hashValue));
  }, [currentHash]);

  const buildHash = (hashes: (string | string[])[]) =>
    `#${[...new Set(hashes.flat())].filter((t) => t).join(separator)}`;

  const popInstead = isCollapsed
    ? buildHash([additionalHashes, hashValue])
    : collapseAllPath;

  const popUpon = isCollapsed
    ? buildHash([additionalHashes, currentHashArray, hashValue])
    : collapsePath;

  return {
    isCollapsed,
    collapseAll,
    collapse,
    collapseAsBack,
    popInstead,
    popUpon,
  };
};
